import { useState, useEffect, useRef } from "react";

import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { ListBox } from "primereact/listbox";
import { TabMenu } from "primereact/tabmenu";
import { BlockUI } from "primereact/blockui";
import StopWatch from "./StopWatch";
import Sport from "./Sport";
import SportsList from "./SportsList";
import { Dialog } from "primereact/dialog";
import { OverlayPanel } from "primereact/overlaypanel";

import ParticipantEditor from "./participants/ParticipantEditor";
import NoteEditor from "./notes/NoteEditor";

import Leaderboard from "./leaderboard/Leaderboard";

import { fetchResults } from "./utils/remoteUtils";
import { updateRemoteResults } from "./utils/remoteUtils";
import { refreshLock } from "./utils/remoteUtils";

import "primereact/resources/themes/lara-light-cyan/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "./App.css";

import data from "./db.json"; // adjust path as needed

const EARLIEST_YEAR = 2024; // first year the event has data for

function App() {
  const [lockInfo, setLockInfo] = useState(null);
  const [locked, setLocked] = useState(true);
  const lockedRef = useRef(locked);

  const [editSports, setEditSports] = useState(false);
  const yearPanelRef = useRef(null);

  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - EARLIEST_YEAR + 1 },
    (_, i) => String(EARLIEST_YEAR + i)
  ).reverse();
  const [year, setYear] = useState(String(currentYear));
  const [sport, setsport] = useState(0);
  const [tab, setTab] = useState(0);

  const [results, setResultsState] = useState(data);

  useEffect(() => {
    setLocked(true);
    refreshLock(setLocked, setLockInfo);
    fetchResults(setResultsState, year);
  }, [year]);

  useEffect(() => {
    // Add focus event listener to trigger fetch when component gains focus
    const handleFocus = () => {
      setLocked(true);
      refreshLock(setLocked, setLockInfo);
      fetchResults(setResultsState, year);
    };
    window.addEventListener("focus", handleFocus);
    return () => {
      // Cleanup: remove focus event listener
      window.removeEventListener("focus", handleFocus);
    };
  }, [year]);

  const setResults = async (newResults) => {
    setResultsState(newResults); // update local state immediately
    try {
      await updateRemoteResults(newResults); // send to remote
    } catch (err) {
      console.error("Failed to update remote results:", err);
    }
  };

  useEffect(() => {
    lockedRef.current = locked;
  }, [locked]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (document.hasFocus()) {
        refreshLock(setLocked, setLockInfo);
        if (lockedRef.current) {
          fetchResults(setResultsState, year);
        }
      }
    }, 5000); // every 3 seconds

    return () => clearInterval(interval); // cleanup on unmount
  }, [year]);

  const tabs = [
    { label: "Osallistujat", command: () => setTab(0) },
    { label: "Lajit", command: () => setTab(1) },
    { label: "Kokonaistilanne", command: () => setTab(2) },
    { label: "Pöytäkirja", command: () => setTab(3) },
  ];

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto" }}>
      <div className="statusbar"></div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "1rem",
          margin: "10px",
          padding: "3px",
        }}
      >
        <img src="/jallu.jpg" alt="Left" style={{ height: "40px" }} />

        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flex: 1,
          }}
        >
          <h1 style={{ margin: 0, textAlign: "center" }}>{year}</h1>
          <Button
            icon="pi pi-cog"
            className="p-button-text p-button-rounded"
            onClick={(e) => yearPanelRef.current.toggle(e)}
            style={{
              position: "absolute",
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
            }}
          />
        </div>
        <OverlayPanel ref={yearPanelRef}>
          <Dropdown
            id="year"
            onChange={(e) => {
              setYear(e.value);
              yearPanelRef.current.hide();
            }}
            options={years}
            value={year}
            placeholder="Select a year"
            className="w-48"
          />
        </OverlayPanel>

        <img src="/jallu.jpg" alt="Right" style={{ height: "40px" }} />
      </div>

      <div style={{ display: "flex", justifyContent: "center" }}>
        <TabMenu model={tabs} activeIndex={tab} />
      </div>

      <div>
        {tab == 0 && (
          <BlockUI
            blocked={locked}
            template={
              <div>
                <i
                  className="pi pi-lock"
                  style={{ fontSize: "3rem", marginBottom: "1rem" }}
                ></i>
              </div>
            }
          >
            <div>
              <ParticipantEditor results={results} setResults={setResults} />
            </div>
          </BlockUI>
        )}
        {tab == 1 && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                margin: "10px",
                position: "relative",
              }}
            >
              <h2 style={{ margin: 0 }}>
                {results.sports.length === 0
                  ? "Ei lajeja"
                  : results.sports[sport]?.name || "Nimetön"}
              </h2>

              <Button
                icon="pi pi-cog"
                className="p-button-text p-button-md"
                onClick={() => {
                  setTab(1);
                  setEditSports(true);
                }}
                style={{
                  position: "absolute",
                  right: 0,
                  top: "50%",
                  transform: "translateY(-50%)",
                }}
              />
            </div>
            <BlockUI
              blocked={locked}
              template={
                <div>
                  <i
                    className="pi pi-lock"
                    style={{ fontSize: "3rem", marginBottom: "1rem" }}
                  ></i>
                </div>
              }
            >
              <Sport
                sports={results}
                selectedSport={sport}
                results={results}
                setResults={setResults}
              />
            </BlockUI>

            <Dialog
              visible={editSports}
              onHide={() => setEditSports(false)}
              style={{ width: "500px" }}
              dismissableMask={true}
            >
              <SportsList
                results={results}
                locked={locked}
                setResults={setResults}
                sport={sport}
                setsport={setsport}
                setEditSports={setEditSports}
              />
            </Dialog>
          </div>
        )}
        {tab == 2 && <Leaderboard results={results} />}
        {tab == 3 && (
          <BlockUI
            blocked={locked}
            template={
              <div>
                <i
                  className="pi pi-lock"
                  style={{ fontSize: "3rem", marginBottom: "1rem" }}
                ></i>
              </div>
            }
          >
            <div>
              <NoteEditor results={results} setResults={setResults} />
            </div>
          </BlockUI>
        )}
      </div>
    </div>
  );
}

export default App;
